import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  SlidersHorizontal, X, ChevronDown, ArrowUpDown, Sparkles, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/context/LanguageContext";

export type SortOption = "newest" | "oldest" | "price-asc" | "price-desc" | "name-asc" | "name-desc";

export interface FilterState {
  categories: string[];
  priceRange: [number, number];
  sort: SortOption;
}

interface CollectionFiltersProps {
  categories: string[];
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  maxPrice: number;
  productCount: number;
}

const FilterSection = ({ title, isOpen, onToggle, children }: { title: string; isOpen: boolean; onToggle: () => void; children: React.ReactNode }) => (
  <div className="border-b border-border/50">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-4 group">
      <span className="text-xs font-medium tracking-[0.2em] uppercase text-foreground/80 group-hover:text-primary transition-colors">{title}</span>
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25, ease: "easeInOut" }}>
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      </motion.div>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: "easeInOut" }} className="overflow-hidden">
          <div className="pb-5">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FilterContent = ({ categories, filters, onFiltersChange, maxPrice, productCount, showSort = true }: CollectionFiltersProps & { showSort?: boolean }) => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(["category", "price", "sort"]));
  const { t } = useLanguage();

  const toggleSection = (section: string) => {
    setOpenSections((prev) => { const next = new Set(prev); if (next.has(section)) next.delete(section); else next.add(section); return next; });
  };

  const activeFilterCount = (filters.categories.length > 0 ? 1 : 0) + (filters.priceRange[0] > 0 || filters.priceRange[1] < maxPrice ? 1 : 0) + (filters.sort !== "newest" ? 1 : 0);

  const allLabel = t.common.all;

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: "newest", label: t.collection.sortOptions.newest },
    { value: "oldest", label: t.collection.sortOptions.oldest },
    { value: "price-asc", label: t.collection.sortOptions.priceAsc },
    { value: "price-desc", label: t.collection.sortOptions.priceDesc },
    { value: "name-asc", label: t.collection.sortOptions.nameAsc },
    { value: "name-desc", label: t.collection.sortOptions.nameDesc },
  ];

  const clearAll = () => { onFiltersChange({ categories: [], priceRange: [0, maxPrice], sort: "newest" }); };

  return (
    <div className="space-y-0">
      <AnimatePresence>
        {activeFilterCount > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="flex items-center justify-between pb-4 mb-1">
              <span className="text-[11px] tracking-[0.15em] uppercase text-muted-foreground font-medium">{activeFilterCount} {t.collection.active}</span>
              <button onClick={clearAll} className="text-[11px] tracking-wider uppercase text-primary/80 hover:text-primary transition-colors">{t.collection.resetAll}</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FilterSection title={t.collection.category} isOpen={openSections.has("category")} onToggle={() => toggleSection("category")}>
        <div className="space-y-1">
          {categories.map((cat) => {
            const isActive = cat === allLabel ? filters.categories.length === 0 : filters.categories.includes(cat);
            return (
              <button key={cat} onClick={() => {
                if (cat === allLabel) { onFiltersChange({ ...filters, categories: [] }); }
                else { const newCats = isActive ? filters.categories.filter(c => c !== cat) : [...filters.categories, cat]; onFiltersChange({ ...filters, categories: newCats }); }
              }} className={`w-full text-left px-3 py-2.5 text-sm rounded-lg transition-all flex items-center gap-2.5 ${isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"}`}>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${isActive ? "border-primary bg-primary" : "border-border"}`}>
                  {isActive && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                </div>
                {cat}
              </button>
            );
          })}
        </div>
      </FilterSection>

      <FilterSection title={t.collection.priceRange} isOpen={openSections.has("price")} onToggle={() => toggleSection("price")}>
        <div className="px-1 space-y-4">
          <Slider min={0} max={maxPrice} step={10} value={filters.priceRange} onValueChange={(value) => onFiltersChange({ ...filters, priceRange: value as [number, number] })} />
          <div className="flex items-center justify-between">
            <div className="px-3 py-1.5 rounded-md bg-muted/50 border border-border/50">
              <span className="text-xs font-medium text-foreground">{filters.priceRange[0]}</span>
              <span className="text-[10px] text-muted-foreground ml-1">{t.common.egp}</span>
            </div>
            <div className="h-px w-4 bg-border" />
            <div className="px-3 py-1.5 rounded-md bg-muted/50 border border-border/50">
              <span className="text-xs font-medium text-foreground">{filters.priceRange[1]}</span>
              <span className="text-[10px] text-muted-foreground ml-1">{t.common.egp}</span>
            </div>
          </div>
        </div>
      </FilterSection>

      {showSort && (
        <FilterSection title={t.collection.sortBy} isOpen={openSections.has("sort")} onToggle={() => toggleSection("sort")}>
          <div className="space-y-0.5">
            {sortOptions.map((option) => {
              const isActive = filters.sort === option.value;
              return (
                <button key={option.value} onClick={() => onFiltersChange({ ...filters, sort: option.value })} className={`w-full text-left px-3 py-2.5 text-[13px] rounded-md transition-all duration-200 flex items-center gap-2 ${isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"}`}>
                  <div className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${isActive ? "bg-primary scale-100" : "bg-transparent scale-0"}`} />
                  {option.label}
                </button>
              );
            })}
          </div>
        </FilterSection>
      )}

      <motion.div key={productCount} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="pt-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/30 border border-border/40">
          <Sparkles className="w-3 h-3 text-primary" />
          <span className="text-[11px] text-muted-foreground tracking-[0.15em] uppercase font-medium">
            {productCount} {productCount !== 1 ? t.collection.products : t.collection.product}
          </span>
        </div>
      </motion.div>
    </div>
  );
};

const CollectionFilters = (props: CollectionFiltersProps) => {
  const isMobile = useIsMobile();
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const { t } = useLanguage();

  const activeFilterCount = (props.filters.categories.length > 0 ? 1 : 0) + (props.filters.priceRange[0] > 0 || props.filters.priceRange[1] < props.maxPrice ? 1 : 0);
  const hasActiveSort = props.filters.sort !== "newest";

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: "newest", label: t.collection.sortOptions.newest },
    { value: "oldest", label: t.collection.sortOptions.oldest },
    { value: "price-asc", label: t.collection.sortOptions.priceAsc },
    { value: "price-desc", label: t.collection.sortOptions.priceDesc },
    { value: "name-asc", label: t.collection.sortOptions.nameAsc },
    { value: "name-desc", label: t.collection.sortOptions.nameDesc },
  ];

  if (isMobile) {
    return (
      <div className="w-full mb-6 space-y-3">
        <div className="flex items-center gap-2">
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 flex-1 h-11 rounded-lg border-border/60 bg-card/50 backdrop-blur-sm">
                <SlidersHorizontal className="w-4 h-4" />
                <span className="text-xs tracking-wider uppercase">{t.collection.filters}</span>
                {activeFilterCount > 0 && <span className="ml-auto px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary text-primary-foreground min-w-[20px] text-center">{activeFilterCount}</span>}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[340px] overflow-y-auto border-r border-border/50">
              <SheetHeader className="pb-2">
                <SheetTitle className="font-serif text-xl flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-primary" />
                  {t.collection.filters}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4"><FilterContent {...props} showSort={false} /></div>
              <div className="sticky bottom-0 pt-4 pb-2 bg-background border-t border-border/50 mt-6">
                <SheetClose asChild>
                  <Button className="w-full" variant="bronze">
                    {t.collection.showProducts} {props.productCount} {props.productCount !== 1 ? t.collection.products : t.collection.product}
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>

          <Sheet open={sortOpen} onOpenChange={setSortOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 flex-1 h-11 rounded-lg border-border/60 bg-card/50 backdrop-blur-sm">
                <ArrowUpDown className="w-4 h-4" />
                <span className="text-xs tracking-wider uppercase">{t.collection.sort}</span>
                {hasActiveSort && <span className="ml-auto px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary text-primary-foreground min-w-[20px] text-center">1</span>}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl pb-safe">
              <SheetHeader className="pb-1"><SheetTitle className="font-serif text-xl">{t.collection.sortBy}</SheetTitle></SheetHeader>
              <div className="space-y-0.5 mt-4 pb-4">
                {sortOptions.map((option) => {
                  const isActive = props.filters.sort === option.value;
                  return (
                    <button key={option.value} onClick={() => { props.onFiltersChange({ ...props.filters, sort: option.value }); setSortOpen(false); }} className={`w-full text-left px-4 py-3.5 text-sm rounded-lg transition-all flex items-center gap-3 ${isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isActive ? "border-primary bg-primary" : "border-border"}`}>
                        {isActive && <Check className="w-3 h-3 text-primary-foreground" />}
                      </div>
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <AnimatePresence>
          {(activeFilterCount > 0 || hasActiveSort) && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="flex flex-wrap gap-2">
                {props.filters.categories.length > 0 && props.filters.categories.map(cat => (
                  <span key={cat} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider uppercase bg-primary/10 text-primary rounded-full border border-primary/20">
                    {cat}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => props.onFiltersChange({ ...props.filters, categories: props.filters.categories.filter(c => c !== cat) })} />
                  </span>
                ))}
                {(props.filters.priceRange[0] > 0 || props.filters.priceRange[1] < props.maxPrice) && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider bg-primary/10 text-primary rounded-full border border-primary/20">
                    {props.filters.priceRange[0]}–{props.filters.priceRange[1]} {t.common.egp}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => props.onFiltersChange({ ...props.filters, priceRange: [0, props.maxPrice] })} />
                  </span>
                )}
                {hasActiveSort && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider bg-primary/10 text-primary rounded-full border border-primary/20">
                    {sortOptions.find((s) => s.value === props.filters.sort)?.label}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => props.onFiltersChange({ ...props.filters, sort: "newest" })} />
                  </span>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-primary" />
          <span className="text-[11px] text-muted-foreground tracking-[0.12em] uppercase">
            {props.productCount} {props.productCount !== 1 ? t.collection.products : t.collection.product}
          </span>
        </div>
      </div>
    );
  }

  return (
    <motion.aside initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: "easeOut" }} className="w-[260px] flex-shrink-0 sticky top-28 self-start">
      <div className="rounded-xl border border-border/40 bg-card/60 backdrop-blur-md p-5 shadow-[0_8px_32px_hsl(0_0%_0%/0.15)]">
        <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-border/40">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <SlidersHorizontal className="w-4 h-4 text-primary" />
          </div>
          <h2 className="font-serif text-lg text-foreground">{t.collection.refine}</h2>
        </div>
        <FilterContent {...props} />
      </div>
    </motion.aside>
  );
};

export default CollectionFilters;
